import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CopyToClipboard,
  Header,
  Link,
  Modal,
  Popover,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
  TableProps,
  Tabs,
} from "@amzn/awsui-components-react";
import { CodeView } from "@amzn/awsui-code-view";
import {
  ClassificationEntry,
  FinancialAmountRow,
  ShadowTestResultResponse,
  ShadowTestDiscrepancy,
  ShadowTestLine,
  ShadowTestMatchSet,
  Stage,
} from "../../lusca-search-api/generated-src";
import { COUNTRY_TO_MARKETPLACE } from "../../helpers/marketplaceUtils";
import { downloadShadowTestCSV } from "../../helpers/csvExportUtils";
import { createFlashUIMatcherLink } from "../../helpers/linkUtils";
import {
  groupShadowTestItemsByGti,
  ShadowTestGtiGroup,
} from "../../helpers/shadowTestBundleUtils";

interface BundleTableRow<T> {
  id: string;
  kind: "group" | "item";
  item?: T;
  group?: ShadowTestGtiGroup<T>;
  children: BundleTableRow<T>[];
}

const createBundleRows = <T,>(
  groups: ShadowTestGtiGroup<T>[]
): BundleTableRow<T>[] =>
  groups.map((group, groupIndex) => {
    const groupId = `group-${group.gti || "unassigned"}-${groupIndex}`;
    return {
      id: groupId,
      kind: "group",
      group,
      children: group.items.map((item, itemIndex) => ({
        id: `${groupId}-item-${itemIndex}`,
        kind: "item",
        item,
        children: [],
      })),
    };
  });

const createBundleColumns = <T,>(
  columnDefinitions: TableProps.ColumnDefinition<T>[]
): TableProps.ColumnDefinition<BundleTableRow<T>>[] =>
  columnDefinitions.map((column, index) => ({
    id: column.id,
    header: column.header,
    width: column.width,
    minWidth: column.minWidth,
    maxWidth: column.maxWidth,
    isRowHeader: column.isRowHeader,
    verticalAlign: column.verticalAlign,
    hasDynamicContent: column.hasDynamicContent,
    cell: (row) => {
      if (row.kind === "item" && row.item) {
        return column.cell(row.item);
      }
      if (index > 0 || !row.group) {
        return null;
      }

      const { group } = row;
      const label = group.gti ? `GTI ${group.gti}` : "No GTI";
      const lineLabel = `${group.items.length} ${
        group.items.length === 1 ? "line" : "lines"
      }`;
      const usecaseLabel = `${group.usecases.length} ${
        group.usecases.length === 1 ? "usecase" : "usecases"
      }`;

      return (
        <span
          style={{
            display: "inline-block",
            whiteSpace: "nowrap",
          }}
        >
          {group.passed !== undefined && (
            <>
              <StatusIndicator type={group.passed ? "success" : "error"}>
                {group.passed ? "PASS" : "FAIL"}
              </StatusIndicator>
              <Box variant="span" color="text-body-secondary">
                {" "}
                ·{" "}
              </Box>
            </>
          )}
          <Box variant="strong" display="inline">
            {label}
          </Box>
          <Box variant="span" color="text-body-secondary">
            {" "}
            · {lineLabel} · {usecaseLabel}
          </Box>
        </span>
      );
    },
  }));

function BundleAwareTable<
  T extends { gti?: string; passed?: boolean; usecase?: string }
>({
  isBundle,
  items,
  columnDefinitions,
  wrapLines,
}: {
  isBundle?: boolean;
  items: T[];
  columnDefinitions: TableProps.ColumnDefinition<T>[];
  wrapLines?: boolean;
}) {
  const [expandedItems, setExpandedItems] = useState<BundleTableRow<T>[]>([]);
  const groupedRows = useMemo(
    () => createBundleRows(groupShadowTestItemsByGti(items)),
    [items]
  );
  const groupedColumns = useMemo(
    () => createBundleColumns(columnDefinitions),
    [columnDefinitions]
  );

  if (!isBundle) {
    return (
      <Table<T>
        items={items}
        columnDefinitions={columnDefinitions}
        variant="embedded"
        wrapLines={wrapLines}
      />
    );
  }

  return (
    <Table<BundleTableRow<T>>
      items={groupedRows}
      trackBy="id"
      columnDefinitions={groupedColumns}
      expandableRows={{
        getItemChildren: (row) => row.children,
        isItemExpandable: (row) => row.kind === "group",
        expandedItems,
        onExpandableItemToggle: ({ detail }) =>
          setExpandedItems((currentItems) =>
            detail.expanded
              ? [...currentItems, detail.item]
              : currentItems.filter((item) => item.id !== detail.item.id)
          ),
        getItemsCount: (row) => row.children.length,
      }}
      ariaLabels={{
        expandButtonLabel: (row) => `Expand ${row.group?.gti || "No GTI"}`,
        collapseButtonLabel: (row) => `Collapse ${row.group?.gti || "No GTI"}`,
      }}
      variant="embedded"
      wrapLines={wrapLines}
    />
  );
}

interface ShadowTestResultModalProps {
  visible: boolean;
  onDismiss: () => void;
  result: ShadowTestResultResponse | null | undefined;
  isLoading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  onRefresh: () => void;
  cliCommand: string;
  classifications: Record<string, ClassificationEntry>;
  onTriggerTest: () => void;
  isTriggerLoading: boolean;
  triggerError: Error | null;
  canTrigger: boolean;
}

export default function ShadowTestResultModal({
  visible,
  onDismiss,
  result,
  isLoading,
  isRefreshing,
  error,
  onRefresh,
  cliCommand,
  classifications,
  onTriggerTest,
  isTriggerLoading,
  triggerError,
  canTrigger,
}: ShadowTestResultModalProps) {
  const [triggerSuccess, setTriggerSuccess] = useState(false);
  const [triggerFailed, setTriggerFailed] = useState(false);
  const wasTriggerLoadingRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      setTriggerSuccess(false);
      setTriggerFailed(false);
      wasTriggerLoadingRef.current = false;
    }
  }, [visible]);

  useEffect(() => {
    setTriggerSuccess(false);
    setTriggerFailed(false);
  }, [result]);

  useEffect(() => {
    if (isTriggerLoading) {
      wasTriggerLoadingRef.current = true;
      setTriggerSuccess(false);
      setTriggerFailed(false);
    } else if (wasTriggerLoadingRef.current) {
      wasTriggerLoadingRef.current = false;
      if (triggerError) {
        setTriggerFailed(true);
      } else {
        setTriggerSuccess(true);
      }
    }
  }, [isTriggerLoading, triggerError]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      );
    }

    if (error) {
      return (
        <SpaceBetween size="l">
          <Box textAlign="center" padding="l">
            <StatusIndicator type="error">
              {error.message || "An unexpected error occurred."}
            </StatusIndicator>
          </Box>
          <Box textAlign="center">
            <Button iconName="refresh" onClick={onRefresh}>
              Retry
            </Button>
          </Box>
          <CliCommandSection cliCommand={cliCommand} />
        </SpaceBetween>
      );
    }

    if (!result) {
      return (
        <SpaceBetween size="l">
          <Box textAlign="center" padding="l" color="text-status-inactive">
            No shadow test results found for this transaction.
          </Box>
          <Box textAlign="center">
            <SpaceBetween size="xs">
              <Button
                variant="primary"
                onClick={onTriggerTest}
                loading={isTriggerLoading}
                disabled={!canTrigger}
              >
                Run Shadow Test
              </Button>
              {triggerSuccess && (
                <StatusIndicator type="success">Test triggered</StatusIndicator>
              )}
              {triggerFailed && (
                <StatusIndicator type="error">Test failed</StatusIndicator>
              )}
            </SpaceBetween>
          </Box>
          <CliCommandSection cliCommand={cliCommand} />
        </SpaceBetween>
      );
    }

    const testDate = result.testTimestamp
      ? new Date(result.testTimestamp).toLocaleString()
      : "";

    const tabs: { id: string; label: string; content: JSX.Element }[] = [];

    if (result.matchedLines && result.matchedLines.length > 0) {
      tabs.push({
        id: "matched",
        label: `Matched (${result.matchedLines.length})`,
        content: (
          <LineTable
            isBundle={result.isBundle}
            lines={result.matchedLines}
            classifications={classifications}
          />
        ),
      });
    }

    if (result.gammaUniqueLines && result.gammaUniqueLines.length > 0) {
      tabs.push({
        id: "gamma-unique",
        label: `Gamma Unique (${result.gammaUniqueLines.length})`,
        content: (
          <LineTable
            isBundle={result.isBundle}
            lines={result.gammaUniqueLines}
            classifications={classifications}
          />
        ),
      });
    }

    if (result.prodUniqueLines && result.prodUniqueLines.length > 0) {
      tabs.push({
        id: "prod-unique",
        label: `Prod Unique (${result.prodUniqueLines.length})`,
        content: (
          <LineTable
            isBundle={result.isBundle}
            lines={result.prodUniqueLines}
            classifications={classifications}
          />
        ),
      });
    }

    if (result.jemUniqueLines && result.jemUniqueLines.length > 0) {
      tabs.push({
        id: "jem-unique",
        label: `JEM Unique (${result.jemUniqueLines.length})`,
        content: (
          <LineTable
            isBundle={result.isBundle}
            lines={result.jemUniqueLines}
            classifications={classifications}
          />
        ),
      });
    }

    if (result.discrepancies && result.discrepancies.length > 0) {
      tabs.push({
        id: "discrepancies",
        label: `Discrepancies (${result.discrepancies.length})`,
        content: (
          <DiscrepancyTable
            isBundle={result.isBundle}
            discrepancies={result.discrepancies}
            classifications={classifications}
          />
        ),
      });
    }

    if (result.matchSets && result.matchSets.length > 0) {
      tabs.push({
        id: "match-sets",
        label: `Match Sets (${result.matchSets.length})`,
        content: (
          <MatchSetTable
            matchSets={result.matchSets}
            countryCode={result.countryCode}
            classifications={classifications}
          />
        ),
      });
    }

    if (result.gammaPivot || result.prodPivot || result.financialAmounts) {
      tabs.push({
        id: "account-balance",
        label: "Financial Validation",
        content: (
          <SpaceBetween size="l">
            {result.financialAmounts &&
              Object.keys(result.financialAmounts).length > 0 && (
                <SpaceBetween size="xs">
                  <Box variant="h4">Financial Amounts</Box>
                  <FinancialAmountsTable rows={result.financialAmounts} />
                </SpaceBetween>
              )}
            {result.gammaPivot && Object.keys(result.gammaPivot).length > 0 && (
              <SpaceBetween size="xs">
                <Box variant="h4">Account Balance Pivot - Gamma</Box>
                <PivotTable pivot={result.gammaPivot} />
              </SpaceBetween>
            )}
            {result.prodPivot && Object.keys(result.prodPivot).length > 0 && (
              <SpaceBetween size="xs">
                <Box variant="h4">Account Balance Pivot - Prod</Box>
                <PivotTable pivot={result.prodPivot} />
              </SpaceBetween>
            )}
          </SpaceBetween>
        ),
      });
    }

    const usecaseComparison: {
      label: string;
      gamma: string;
      prod: string;
      jem: string;
    }[] = [
      {
        label: "Usecases",
        gamma: result.gammaUsecases?.join(", ") || "-",
        prod: result.prodUsecases?.join(", ") || "-",
        jem: result.jemUsecases?.join(", ") || "-",
      },
      {
        label: "Lines",
        gamma: String(result.totalGammaLines ?? 0),
        prod: String(result.totalProdLines ?? 0),
        jem: "-",
      },
    ];

    return (
      <SpaceBetween size="s">
        <SpaceBetween size="xs" direction="horizontal">
          <StatusIndicator type={result.passed ? "success" : "error"}>
            {result.passed ? "PASS" : "FAIL"}
          </StatusIndicator>
          {result.sourceAmountCheckPassed != null && (
            <>
              <Box color="text-body-secondary">·</Box>
              <StatusIndicator
                type={result.sourceAmountCheckPassed ? "success" : "error"}
              >
                {result.sourceAmountCheckPassed
                  ? "Amounts Match"
                  : "Amount Mismatch"}
              </StatusIndicator>
            </>
          )}
          <Box color="text-body-secondary">·</Box>
          <Box>GL {result.glProductGroup}</Box>
          <Box color="text-body-secondary">·</Box>
          <Box>{result.countryCode}</Box>
          <Box color="text-body-secondary">·</Box>
          <Box>{result.paymentProvider || "-"}</Box>
          {result.isPromo && (
            <>
              <Box color="text-body-secondary">·</Box>
              <Box color="text-status-warning">Promo</Box>
            </>
          )}
          {result.isBundle && (
            <>
              <Box color="text-body-secondary">·</Box>
              <Box>Bundle</Box>
            </>
          )}
          <Box color="text-body-secondary">·</Box>
          <Box color="text-body-secondary">Tested {testDate}</Box>
        </SpaceBetween>

        <Table
          items={usecaseComparison}
          columnDefinitions={[
            {
              id: "label",
              header: "",
              cell: (e) => <Box fontWeight="bold">{e.label}</Box>,
              width: 100,
            },
            { id: "gamma", header: "Gamma", cell: (e) => e.gamma },
            { id: "prod", header: "Prod", cell: (e) => e.prod },
            ...(result.jemUsecases && result.jemUsecases.length > 0
              ? [
                  {
                    id: "jem",
                    header: "JEM",
                    cell: (e: (typeof usecaseComparison)[0]) => e.jem,
                  },
                ]
              : []),
          ]}
          variant="embedded"
          wrapLines
        />

        {tabs.length > 0 && <Tabs tabs={tabs} />}

        <CliCommandSection cliCommand={cliCommand} />
      </SpaceBetween>
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="max"
      header={
        <Header
          actions={
            <SpaceBetween size="xs" direction="horizontal">
              <Button
                iconName="refresh"
                onClick={onRefresh}
                loading={isRefreshing}
              >
                Refresh
              </Button>
              {result && (
                <>
                  <Button
                    iconName="download"
                    onClick={() =>
                      downloadShadowTestCSV(result, classifications)
                    }
                  >
                    Export CSV
                  </Button>
                  <Button
                    onClick={onTriggerTest}
                    loading={isTriggerLoading}
                    disabled={!canTrigger}
                  >
                    Re-run Test
                  </Button>
                  {triggerSuccess && (
                    <StatusIndicator type="success">
                      Test triggered
                    </StatusIndicator>
                  )}
                  {triggerFailed && (
                    <StatusIndicator type="error">Test failed</StatusIndicator>
                  )}
                </>
              )}
            </SpaceBetween>
          }
        >
          Shadow Test Result
        </Header>
      }
    >
      {renderContent()}
    </Modal>
  );
}

const ClassificationCell = ({
  classification,
  passed,
  classifications,
}: {
  classification?: string;
  passed?: boolean;
  classifications: Record<string, ClassificationEntry>;
}) => {
  const indicator = (
    <StatusIndicator type={passed ? "success" : "error"}>
      {classification || "-"}
    </StatusIndicator>
  );

  const desc = classification
    ? classifications[classification]?.description
    : undefined;
  if (!desc) return indicator;

  return (
    <Popover
      dismissButton={false}
      position="top"
      size="medium"
      triggerType="custom"
      content={<Box>{desc}</Box>}
    >
      <span style={{ cursor: "help", borderBottom: "1px dotted" }}>
        {indicator}
      </span>
    </Popover>
  );
};

const lineColumns = (classifications: Record<string, ClassificationEntry>) => [
  {
    id: "usecase",
    header: "Usecase",
    cell: (e: ShadowTestLine) => e.usecase || "-",
  },
  {
    id: "account",
    header: "Account Number",
    cell: (e: ShadowTestLine) => e.accountNumber || "-",
  },
  {
    id: "side",
    header: "Journal Side",
    cell: (e: ShadowTestLine) => e.journalSide || "-",
  },
  {
    id: "companyCode",
    header: "Company Code",
    cell: (e: ShadowTestLine) => e.companyCode || "-",
  },
  {
    id: "location",
    header: "Location",
    cell: (e: ShadowTestLine) => e.location || "-",
  },
  {
    id: "costCenter",
    header: "Cost Center",
    cell: (e: ShadowTestLine) => e.costCenter || "-",
  },
  {
    id: "productLine",
    header: "Product Line",
    cell: (e: ShadowTestLine) => e.productLine || "-",
  },
  {
    id: "channel",
    header: "Channel",
    cell: (e: ShadowTestLine) => e.channel || "-",
  },
  {
    id: "project",
    header: "Project",
    cell: (e: ShadowTestLine) => e.project || "-",
  },
  {
    id: "functionalAmount",
    header: "Functional Amount",
    cell: (e: ShadowTestLine) =>
      e.functionalAmount
        ? `${e.functionalAmount} ${e.functionalCurrency || e.currency || ""}`
        : "-",
  },
  {
    id: "localAmount",
    header: "Local Amount",
    cell: (e: ShadowTestLine) =>
      e.localAmount
        ? `${e.localAmount} ${e.localCurrency || e.currency || ""}`
        : "-",
  },
  {
    id: "description",
    header: "GL Line Description",
    cell: (e: ShadowTestLine) => e.glLineDescription || "-",
  },
  {
    id: "setOfBooks",
    header: "Set of Books",
    cell: (e: ShadowTestLine) => e.setOfBooks || "-",
  },
  {
    id: "matchKey",
    header: "Match Key",
    cell: (e: ShadowTestLine) => e.matchKey || "-",
  },
  {
    id: "intent",
    header: "Intent",
    cell: (e: ShadowTestLine) => e.intent || "-",
  },
  {
    id: "env",
    header: "Environment",
    cell: (e: ShadowTestLine) => e.environment || "-",
  },
  {
    id: "classification",
    header: "Classification",
    cell: (e: ShadowTestLine) => (
      <ClassificationCell
        classification={e.classification}
        passed={e.passed}
        classifications={classifications}
      />
    ),
  },
];

const LineTable = ({
  isBundle,
  lines,
  classifications,
}: {
  isBundle?: boolean;
  lines: ShadowTestLine[];
  classifications: Record<string, ClassificationEntry>;
}) => (
  <div style={{ fontSize: "12px" }}>
    <BundleAwareTable
      isBundle={isBundle}
      items={lines}
      columnDefinitions={lineColumns(classifications)}
      wrapLines
    />
  </div>
);

const discrepancyColumns = (
  classifications: Record<string, ClassificationEntry>
): TableProps.ColumnDefinition<ShadowTestDiscrepancy>[] => [
  { id: "field", header: "Field", cell: (e) => e.field || "-" },
  { id: "usecase", header: "Usecase", cell: (e) => e.usecase || "-" },
  { id: "gammaValue", header: "Gamma", cell: (e) => e.gammaValue || "-" },
  { id: "prodValue", header: "Prod", cell: (e) => e.prodValue || "-" },
  { id: "jemValue", header: "JEM", cell: (e) => e.jemValue || "-" },
  { id: "intent", header: "Intent", cell: (e) => e.intent || "-" },
  { id: "account", header: "Account", cell: (e) => e.accountNumber || "-" },
  {
    id: "classification",
    header: "Classification",
    cell: (e) => (
      <ClassificationCell
        classification={e.classification}
        passed={e.passed}
        classifications={classifications}
      />
    ),
  },
];

const DiscrepancyTable = ({
  isBundle,
  discrepancies,
  classifications,
}: {
  isBundle?: boolean;
  discrepancies: ShadowTestDiscrepancy[];
  classifications: Record<string, ClassificationEntry>;
}) => (
  <BundleAwareTable
    isBundle={isBundle}
    items={discrepancies}
    columnDefinitions={discrepancyColumns(classifications)}
  />
);

const MatchSetTable = ({
  matchSets,
  countryCode,
  classifications,
}: {
  matchSets: ShadowTestMatchSet[];
  countryCode?: string;
  classifications: Record<string, ClassificationEntry>;
}) => (
  <Table<ShadowTestMatchSet>
    items={matchSets}
    columnDefinitions={[
      { id: "env", header: "Env", cell: (e) => e.environment || "-" },
      {
        id: "matchKey",
        header: "Match Key",
        cell: (e) => {
          if (!e.matchKey || !e.accountNumber || !e.environment)
            return e.matchKey || "-";
          const mpId =
            COUNTRY_TO_MARKETPLACE[countryCode || ""] || "ATVPDKIKX0DER";
          const stage =
            e.environment.toLowerCase() === "prod" ? Stage.Prod : Stage.Gamma;
          const url = createFlashUIMatcherLink(
            mpId,
            e.matchKey,
            stage,
            e.accountNumber
          );
          return (
            <Link href={url} external>
              {e.matchKey}
            </Link>
          );
        },
      },
      { id: "account", header: "Account", cell: (e) => e.accountNumber || "-" },
      {
        id: "amount",
        header: "Balance",
        cell: (e) => (e.amount ? `${e.amount} ${e.currency || ""}` : "-"),
      },
      {
        id: "closed",
        header: "Closed",
        cell: (e) => (
          <StatusIndicator type={e.closed ? "success" : "warning"}>
            {e.closed ? "Yes" : "No"}
          </StatusIndicator>
        ),
      },
      {
        id: "classification",
        header: "Classification",
        cell: (e) => (
          <ClassificationCell
            classification={e.classification}
            passed={e.passed}
            classifications={classifications}
          />
        ),
      },
    ]}
    variant="embedded"
  />
);

const formatLabel = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Decide whether a source amount should be flagged as mismatched against a USL amount for the
// Financial Validation tab. When currencies differ we cannot compare on the client (no FX rate
// available here), so suppress row-level red and defer to the backend-computed tab pass/fail.
// When currencies match, compare the string amounts directly (same as before).
const amountMismatch = (
  sourceAmount: string | undefined,
  sourceCurr: string | undefined,
  uslAmount: string | undefined,
  uslCurr: string | undefined
): boolean => {
  if (!sourceAmount || !uslAmount) return false;
  if (sourceCurr && uslCurr && sourceCurr !== uslCurr) return false;
  return sourceAmount !== uslAmount;
};

const FinancialAmountsTable = ({
  rows,
}: {
  rows: { [key: string]: FinancialAmountRow };
}) => (
  <div style={{ fontSize: "12px" }}>
    <Table
      items={Object.entries(rows).map(([key, row]) => ({ key, ...row }))}
      columnDefinitions={[
        {
          id: "element",
          header: "Element",
          cell: (e) => formatLabel(e.key),
          minWidth: 220,
        },
        { id: "source", header: "Source", cell: (e) => e.source || "-" },
        {
          id: "sourceAmount",
          header: "Source Amount",
          cell: (e) => e.sourceAmount || "-",
        },
        {
          id: "sourceCurr",
          header: "Source Curr",
          cell: (e) => e.sourceCurr || "-",
        },
        {
          id: "gammaUslAmount",
          header: "Gamma USL Amount",
          cell: (e) => {
            const mismatch = amountMismatch(
              e.sourceAmount,
              e.sourceCurr,
              e.gammaUslAmount,
              e.gammaUslCurr
            );
            return (
              <Box color={mismatch ? "text-status-error" : undefined}>
                {e.gammaUslAmount || "-"}
              </Box>
            );
          },
        },
        {
          id: "gammaUslCurr",
          header: "Gamma USL Curr",
          cell: (e) => <Box>{e.gammaUslCurr || "-"}</Box>,
        },
        {
          id: "prodUslAmount",
          header: "Prod USL Amount",
          cell: (e) => {
            const mismatch = amountMismatch(
              e.sourceAmount,
              e.sourceCurr,
              e.prodUslAmount,
              e.prodUslCurr
            );
            return (
              <Box color={mismatch ? "text-status-error" : undefined}>
                {e.prodUslAmount || "-"}
              </Box>
            );
          },
        },
        {
          id: "prodUslCurr",
          header: "Prod USL Curr",
          cell: (e) => <Box>{e.prodUslCurr || "-"}</Box>,
        },
      ]}
      variant="embedded"
    />
  </div>
);

const PivotTable = ({
  pivot,
}: {
  pivot: { [key: string]: { [key: string]: number } };
}) => {
  const accounts = Array.from(
    new Set(Object.values(pivot).flatMap((row) => Object.keys(row)))
  ).sort();

  const rows = Object.entries(pivot).sort(([a], [b]) => a.localeCompare(b));

  const colTotals: Record<string, number> = {};
  accounts.forEach((a) => (colTotals[a] = 0));
  rows.forEach(([, cells]) =>
    accounts.forEach(
      (a) => (colTotals[a] = (colTotals[a] ?? 0) + (cells[a] ?? 0))
    )
  );
  const grandTotal = accounts.reduce((s, a) => s + colTotals[a], 0);

  const items = [
    ...rows.map(([glDesc, cells]) => {
      const rowTotal = accounts.reduce((s, a) => s + (cells[a] ?? 0), 0);
      return { glDesc, cells, rowTotal, isTotal: false };
    }),
    {
      glDesc: "Grand Total",
      cells: colTotals,
      rowTotal: grandTotal,
      isTotal: true,
    },
  ];

  const fmt = (n: number) => (n === 0 ? "0" : n.toFixed(2));

  return (
    <div style={{ fontSize: "12px", overflowX: "auto" }}>
      <Table
        items={items}
        columnDefinitions={[
          {
            id: "glDesc",
            header: "GL Line Description",
            cell: (e) => (
              <Box fontWeight={e.isTotal ? "bold" : "normal"}>{e.glDesc}</Box>
            ),
            minWidth: 280,
          },
          ...accounts.map((a) => ({
            id: a,
            header: a,
            cell: (e: (typeof items)[0]) => (
              <Box textAlign="right" fontWeight={e.isTotal ? "bold" : "normal"}>
                {fmt(e.cells[a] ?? 0)}
              </Box>
            ),
          })),
          {
            id: "grandTotal",
            header: "Grand Total",
            cell: (e) => (
              <Box
                textAlign="right"
                fontWeight="bold"
                color={e.rowTotal !== 0 ? "text-status-error" : undefined}
              >
                {fmt(e.rowTotal)}
              </Box>
            ),
          },
        ]}
        variant="embedded"
      />
    </div>
  );
};

const CliCommandSection = ({ cliCommand }: { cliCommand: string }) => (
  <Box>
    <Box variant="h4">Run locally</Box>
    <CodeView
      content={cliCommand}
      actions={
        <CopyToClipboard
          copyButtonAriaLabel="Copy command"
          copyErrorText="Failed to copy"
          copySuccessText="Copied"
          textToCopy={cliCommand}
        />
      }
    />
  </Box>
);
